import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PaperAirplaneIcon } from '../components/Icons';
import { User } from '../types';

const LoginScreen: React.FC = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const { login, register, isRegistrationOpen, appAssets, verifyPinAndLogin, verifyOtpAndLogin } = useAuth();
  
  // Login flow state
  const [loginStep, setLoginStep] = useState<'credentials' | 'pin' | 'otp'>('credentials');
  const [pendingUser, setPendingUser] = useState<User | null>(null);

  // Common state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Registration specific state
  const [email, setEmail] = useState('');
  const [authCode, setAuthCode] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    const result = await login(username, password);
    if (result.success && result.user) {
      setPendingUser(result.user);
      if (result.message === 'OTP required') {
        setLoginStep('otp');
      } else if (result.message === 'PIN verification required.') {
        setLoginStep('pin');
      }
      // If no result.user, login happened directly, App will handle redirect.
    } else if (!result.success) {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingUser) return;
    setIsLoading(true);
    setError('');
    const result = await verifyPinAndLogin(pendingUser, pin);
    if (!result.success) {
      setError(result.message);
    }
    setIsLoading(false);
    // On success, App component will handle the redirect.
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingUser) return;
    setIsLoading(true);
    setError('');
    const result = await verifyOtpAndLogin(pendingUser, otpCode);
    if (!result.success) {
      setError(result.message);
    }
    setIsLoading(false);
     // On success, App component will handle the redirect.
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if(password.length < 6) {
        setError('Password must be at least 6 characters long.');
        return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    const result = await register(username, password, email, authCode);
    if (result.success) {
        setSuccess(result.message);
        setIsRegistering(false); // Switch back to login view
        // Clear fields
        setUsername('');
        setPassword('');
        setEmail('');
        setAuthCode('');
    } else {
        setError(result.message);
    }
    setIsLoading(false);
  };

  const toggleView = () => {
      setIsRegistering(!isRegistering);
      setError('');
      setSuccess('');
      setUsername('');
      setPassword('');
      setPin('');
      setOtpCode('');
      setLoginStep('credentials');
  };

  const renderHeader = (title: string, subtitle: string) => (
     <div className="text-center">
        <div className="flex justify-center items-center gap-4 mb-6 h-16">
          {appAssets.logo1 && <img src={appAssets.logo1} alt="Primary Logo" className="max-h-full max-w-[45%] object-contain" />}
          {appAssets.logo2 && <img src={appAssets.logo2} alt="Secondary Logo" className="max-h-full max-w-[45%] object-contain" />}
        </div>
        <PaperAirplaneIcon className="mx-auto h-16 w-auto text-blue-500 -rotate-45 animate-pulse" style={{ animationDuration: '3s' }}/>
        <h2 className="mt-6 text-3xl font-extrabold text-slate-100">
            {title}
        </h2>
        <p className="mt-2 text-sm text-slate-400">
            {subtitle}
        </p>
    </div>
  );
  
  const renderCredentialForms = () => {
    if (isRegistering) {
        return (
            <form className="mt-8 space-y-6" onSubmit={handleRegister}>
                <div className="space-y-4">
                    <input value={username} onChange={(e) => setUsername(e.target.value)} id="username-reg" placeholder="Username" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} id="email-reg" placeholder="Email" type="email" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                    <input value={password} onChange={(e) => setPassword(e.target.value)} id="password-reg" placeholder="Password" type="password" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                    <input value={authCode} onChange={(e) => setAuthCode(e.target.value)} id="authcode-reg" placeholder="Authorization Code" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                </div>
                {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
                <div>
                    <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-slate-900 disabled:bg-slate-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                        {isLoading ? 'Registering...' : 'Register'}
                    </button>
                </div>
             </form>
        );
    }
    return (
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
                <input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required className="appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input" />
            </div>
            {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
            {success && <p className="text-green-400 text-sm text-center pt-2">{success}</p>}
            <div>
                <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-slate-900 disabled:bg-slate-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                    {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
            </div>
        </form>
    );
  };
  
  const renderPinForm = () => (
     <form className="mt-8 space-y-6" onSubmit={handlePinSubmit}>
        <div className="space-y-4">
            <input 
              id="pin" 
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin} 
              onChange={(e) => setPin(e.target.value)} 
              placeholder="Security PIN" 
              required 
              className="text-center tracking-[1em] text-2xl font-bold appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input"
            />
        </div>
        {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
        <div>
            <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-slate-900 disabled:bg-slate-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                {isLoading ? 'Verifying...' : 'Verify'}
            </button>
        </div>
    </form>
  );

  const renderOtpForm = () => (
     <form className="mt-8 space-y-6" onSubmit={handleOtpSubmit}>
        <div className="space-y-4">
            <input 
              id="otp" 
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode} 
              onChange={(e) => setOtpCode(e.target.value)} 
              placeholder="6-Digit Code" 
              required 
              className="text-center tracking-[0.5em] text-2xl font-bold appearance-none relative block w-full px-3 py-3 placeholder-slate-400 sm:text-sm futuristic-input"
            />
        </div>
        {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
        <div>
            <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-slate-900 disabled:bg-slate-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                {isLoading ? 'Verifying...' : 'Verify'}
            </button>
        </div>
    </form>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-900/60 backdrop-blur-xl rounded-2xl shadow-2xl p-8 space-y-8 border border-blue-500/20 shadow-[0_0_25px_rgba(59,130,246,0.1)]">
        {loginStep === 'credentials' && (isRegistering ? renderHeader('JT-Crew Movement', 'Create a new account') : renderHeader('JT-Crew Movement', 'Sign in to your account'))}
        {loginStep === 'pin' && renderHeader('Security PIN', `Enter the 4-digit PIN for ${pendingUser?.username}`)}
        {loginStep === 'otp' && renderHeader('Two-Factor Authentication', `Enter the code from your authenticator app`)}
        
        {loginStep === 'credentials' && renderCredentialForms()}
        {loginStep === 'pin' && renderPinForm()}
        {loginStep === 'otp' && renderOtpForm()}
        

        <div className="text-sm text-center space-y-4 pt-4 border-t border-slate-700/50">
          <button onClick={toggleView} className="font-medium text-blue-400 hover:text-blue-300" disabled={!isRegistrationOpen && !isRegistering}>
            {loginStep !== 'credentials' 
              ? 'Back to login' 
              : isRegistering 
                ? 'Already have an account? Sign in' 
                : (isRegistrationOpen ? "Don't have an account? Register" : "")
            }
          </button>
           <p className="text-xs text-slate-500">
                Crew-movement Developed By John tawfik One Of <span className="font-semibold text-amber-400">EAS</span> Team Members v 7I
            </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;